import React, { useMemo } from 'react';
import { App as AntApp, ConfigProvider } from 'antd';
import { createAdminApi, type AdminApi } from './api';
import { createBillingApi, type BillingApi } from './billingApi';
import { useAdminSession } from './auth';
import { Shell } from './components/Shell';
import { useRoute } from './router';
import { CardsScreen } from './screens/CardsScreen';
import { ComingLaterScreen } from './screens/ComingLaterScreen';
import { DeckEditorScreen } from './screens/DeckEditorScreen';
import { DeckListScreen } from './screens/DeckListScreen';
import { EmailsScreen } from './screens/EmailsScreen';
import { FinanceScreen } from './screens/FinanceScreen';
import {
  CheckErrorScreen,
  LoadingScreen,
  NoAccessScreen,
  SignInScreen,
} from './screens/GateScreens';
import { RevenueScreen } from './screens/RevenueScreen';
import type { AdminSupabase } from './supabase';
import { adminTheme } from './theme';
import { UserScreen } from './screens/UserScreen';
import { UsersScreen } from './screens/UsersScreen';

const AdminRoutes: React.FC<{
  api: AdminApi;
  billing: BillingApi;
  email: string;
  onSignOut: () => void;
}> = ({ api, billing, email, onSignOut }) => {
  const [route, navigate] = useRoute();
  return (
    <Shell route={route} navigate={navigate} email={email} onSignOut={onSignOut}>
      {route.name === 'deck' ? (
        <DeckEditorScreen key={route.id} api={api} deckId={route.id} navigate={navigate} />
      ) : route.name === 'cards' ? (
        <CardsScreen api={api} billing={billing} />
      ) : route.name === 'users' ? (
        <UsersScreen billing={billing} navigate={navigate} />
      ) : route.name === 'user' ? (
        <UserScreen key={route.id} billing={billing} userId={route.id} navigate={navigate} />
      ) : route.name === 'revenue' ? (
        <RevenueScreen billing={billing} />
      ) : route.name === 'finance' ? (
        <FinanceScreen billing={billing} />
      ) : route.name === 'emails' ? (
        <EmailsScreen billing={billing} />
      ) : route.name === 'soon' ? (
        <ComingLaterScreen section={route.section} />
      ) : (
        <DeckListScreen api={api} navigate={navigate} />
      )}
    </Shell>
  );
};

/** Gate (Google sign-in, then `is_admin`) in front of the admin screens. */
export const AdminApp: React.FC<{
  supabase: AdminSupabase;
  api?: AdminApi;
  billing?: BillingApi;
}> = ({ supabase, api: apiOverride, billing: billingOverride }) => {
  const session = useAdminSession(supabase);
  const api = useMemo(() => apiOverride ?? createAdminApi(supabase), [apiOverride, supabase]);
  const billing = useMemo(
    () => billingOverride ?? createBillingApi(supabase),
    [billingOverride, supabase]
  );
  const { state } = session;

  let content: React.ReactNode;
  switch (state.kind) {
    case 'loading':
      content = <LoadingScreen />;
      break;
    case 'signedOut':
      content = <SignInScreen onSignIn={session.signIn} error={state.error} />;
      break;
    case 'checking':
      content = <LoadingScreen label="Checking admin access…" />;
      break;
    case 'notAdmin':
      content = <NoAccessScreen email={state.email} onSignOut={() => void session.signOut()} />;
      break;
    case 'error':
      content = (
        <CheckErrorScreen
          email={state.email}
          message={state.message}
          onRetry={session.retry}
          onSignOut={() => void session.signOut()}
        />
      );
      break;
    case 'admin':
      content = (
        <AdminRoutes
          api={api}
          billing={billing}
          email={state.email}
          onSignOut={() => void session.signOut()}
        />
      );
      break;
  }

  return (
    <ConfigProvider theme={adminTheme}>
      <AntApp>{content}</AntApp>
    </ConfigProvider>
  );
};
